import { IDisposer } from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { EventEmitter } from 'eventemitter3';
import _ from 'lodash';
import { v4 } from 'uuid';

type AxisType = 'value' | 'date';

interface BaseParams {
  chartId: string;
  groupId: string;
  // we only match x axes, y axis can be anything
  xAxisType: AxisType;
}

interface MoveCursorParams extends BaseParams {
  x: number;
  y: number;
}

interface ChangeZoomParams extends BaseParams {
  minZoom: number;
  maxZoom: number;
}

type SetTooltipOnParams = BaseParams;

type SetTooltipOffParams = BaseParams;

export interface SynchronizationEventEmitterTypes {
  moveCursor: (params: MoveCursorParams) => void;
  setTooltipOn: (params: SetTooltipOnParams) => void;
  setTooltipOff: (params: SetTooltipOffParams) => void;
  changeZoom: (params: ChangeZoomParams) => void;
}

export const SynchronizationEventEmitter = new EventEmitter<SynchronizationEventEmitterTypes>();

let lastZoomStartedChartId = '';

export interface SyncAxisCursorParams {
  chart: am5xy.XYChart;
  rootNode: HTMLElement;
  groupId?: string;
}

export function syncAxisCursor({ chart, rootNode, groupId = '' }: SyncAxisCursorParams) {
  // not using web crypto uuid because it works only in HTTPS
  const chartId = v4();
  let isPositionSetToUndefined = true;
  let isPointerOverChart = false;
  let isPointerDownInsideChart = false;

  const cursor = chart.get('cursor');
  const xAxis = chart.xAxes.getIndex(0);
  const yAxis = chart.yAxes.getIndex(0);

  if (!cursor) {
    throw new Error('syncAxisCursor: cursor needs to be set in the chart');
  }

  if (!xAxis) {
    throw new Error('syncAxisCursor: X axis needs to be set in the chart');
  }

  if (!(xAxis instanceof am5xy.ValueAxis || xAxis instanceof am5xy.DateAxis)) {
    throw new Error('syncAxisCursor: X axis needs to be either a ValueAxis or a DateAxis');
  }

  if (!yAxis) {
    throw new Error('syncAxisCursor: Y axis need to be set in the chart');
  }

  if (!(yAxis instanceof am5xy.ValueAxis || yAxis instanceof am5xy.DateAxis)) {
    throw new Error('syncAxisCursor: Y axis needs to be either a ValueAxis or a DateAxis');
  }

  let visibilityDisposers: IDisposer[] = [];

  chart.series.events.on('push', () => {
    visibilityDisposers.forEach((disposer) => {
      if (!disposer.isDisposed()) {
        disposer.dispose();
      }
    });

    visibilityDisposers = [];

    chart.series.each((serie) => {
      const tooltip = serie.get('tooltip');

      if (tooltip) {
        const disposer = tooltip.adapters.add('visible', () => {
          return isPointerOverChart;
        });

        visibilityDisposers.push(disposer);
      }
    });
  });

  const xAxisType: AxisType = xAxis instanceof am5xy.ValueAxis ? 'value' : 'date';

  const handleMouseOver = () => {
    isPointerOverChart = true;
    SynchronizationEventEmitter.emit('setTooltipOn', { chartId, groupId, xAxisType });
  };

  const handleMouseOut = () => {
    isPointerOverChart = false;

    if (!isPointerOverChart && !isPointerDownInsideChart) {
      SynchronizationEventEmitter.emit('setTooltipOff', { chartId, groupId, xAxisType });
    }
  };

  const handleMouseDown = () => {
    isPointerDownInsideChart = true;
  };

  const handleMouseUp = () => {
    const wasPointerDownInsideChart = isPointerDownInsideChart;

    isPointerDownInsideChart = false;

    if (!isPointerOverChart && wasPointerDownInsideChart && !isPointerDownInsideChart) {
      SynchronizationEventEmitter.emit('setTooltipOff', { chartId, groupId, xAxisType });
    }
  };

  rootNode.addEventListener('mouseover', handleMouseOver);
  rootNode.addEventListener('mouseout', handleMouseOut);
  rootNode.addEventListener('mousedown', handleMouseDown);
  // yes, document, it is NOT a mistake, we need to handle a case when
  // the mouse is outside of the window
  document.addEventListener('mouseup', handleMouseUp);

  const disposeCursorEvent = cursor.events.on('cursormoved', (ev) => {
    if (isPointerOverChart) {
      const x = ev.target.getPrivate('positionX')!;
      const y = ev.target.getPrivate('positionY')!;
      const xVal = xAxis.positionToValue(xAxis.toAxisPosition(x));
      const yVal = yAxis.positionToValue(yAxis.toAxisPosition(y));

      SynchronizationEventEmitter.emit('moveCursor', {
        chartId,
        groupId,
        x: xVal,
        y: yVal,
        xAxisType,
      });
    }
  });

  const isValidSourceChart = (params: BaseParams) => {
    return params.chartId !== chartId && params.groupId === groupId && params.xAxisType === xAxisType;
  };

  const handleMoveCursor = (params: MoveCursorParams) => {
    if (isValidSourceChart(params)) {
      isPositionSetToUndefined = false;

      const xAxisStart = xAxis.toAxisPosition(0);
      const xAxisEnd = xAxis.toAxisPosition(1);
      const yAxisStart = yAxis.toAxisPosition(0);
      const yAxisEnd = yAxis.toAxisPosition(1);

      setTimeout(() => {
        cursor.setAll({
          positionX: clamp((xAxis.valueToPosition(params.x) - xAxisStart) / (xAxisEnd - xAxisStart)),
          // y coord is reversed
          positionY: clamp((yAxis.valueToPosition(params.y) - yAxisEnd) / (yAxisStart - yAxisEnd)),
        });
      });
    } else {
      if (!isPositionSetToUndefined) {
        // setting positions to undefined unlocks cursors on the charts
        // so that they can again be set by direct user input and not by JS API
        isPositionSetToUndefined = true;
        cursor.setAll({
          positionX: undefined,
          positionY: undefined,
        });
      }
    }
  };

  SynchronizationEventEmitter.on('moveCursor', handleMoveCursor);

  const handleSetTooltipOn = (params: SetTooltipOnParams) => {
    if (isValidSourceChart(params)) {
      cursor.set('alwaysShow', true);
    }
  };

  const handleSetTooltipOff = (params: SetTooltipOnParams) => {
    if (isValidSourceChart(params)) {
      cursor.set('alwaysShow', false);
    }
  };

  SynchronizationEventEmitter.on('setTooltipOn', handleSetTooltipOn);
  SynchronizationEventEmitter.on('setTooltipOff', handleSetTooltipOff);

  // both value axis and date axis have these methods, but
  // they have incompatible signatures at higher level and that's why
  // we need to cast it to one or the other
  const castedXAxis = xAxis as am5xy.ValueAxis<am5xy.AxisRenderer>;

  const onZoomStartEndChange = _.debounce(() => {
    if (isPointerOverChart || isPointerDownInsideChart || lastZoomStartedChartId === chartId) {
      const minZoom = castedXAxis.getPrivate('selectionMin')!;
      const maxZoom = castedXAxis.getPrivate('selectionMax')!;

      SynchronizationEventEmitter.emit('changeZoom', {
        chartId,
        groupId,
        xAxisType,
        minZoom,
        maxZoom,
      });
    }
  }, 200);

  const disposeZoomStart = castedXAxis.on('start', () => {
    lastZoomStartedChartId = chartId;
    onZoomStartEndChange();
  });
  const disposeZoomEnd = castedXAxis.on('end', () => {
    lastZoomStartedChartId = chartId;
    onZoomStartEndChange();
  });

  const handleChangeZoom = (params: ChangeZoomParams) => {
    if (isValidSourceChart(params)) {
      xAxis.zoomToValues(params.minZoom, params.maxZoom, 0);
    }
  };

  SynchronizationEventEmitter.on('changeZoom', handleChangeZoom);

  return () => {
    SynchronizationEventEmitter.off('changeZoom', handleChangeZoom);
    disposeZoomEnd.dispose();
    disposeZoomStart.dispose();
    SynchronizationEventEmitter.off('setTooltipOff', handleSetTooltipOff);
    SynchronizationEventEmitter.off('setTooltipOn', handleSetTooltipOn);
    SynchronizationEventEmitter.off('moveCursor', handleMoveCursor);
    disposeCursorEvent.dispose();
    rootNode.removeEventListener('mouseover', handleMouseOver);
    rootNode.removeEventListener('mouseout', handleMouseOut);
    rootNode.removeEventListener('mousedown', handleMouseDown);
    rootNode.removeEventListener('mouseup', handleMouseUp);
  };
}

/**
 * Clamps the input value between min and max, e.g:
 *
 * value: 1.02, min = 0, max = 1, result: 1.0
 * value: 0.5, min = 0.3, max = 0.8, result: 0.5
 *
 * @param value
 * @param min defaults to 0
 * @param max defaults to 1
 * @returns
 */
function clamp(value: number, min = 0, max = 1) {
  return Math.min(Math.max(min, value), max);
}
